import React, { useState, useRef, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import DatePicker from 'react-datepicker';
import { parse, format } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import Spinner from '../../../../components/global/Spinner';
import { AVATAR_SIZE_72 } from '../../../../constants/avatar-sizes';
import Button from '../../../../components/global/Button';
import Alert from '../../../../components/global/Alert';
import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import FormGroup from '../../../../components/global/FormGroup';
import useRosterIdentityUpdate from './useRosterIdentityUpdate';
import UserAddressFormModal from '../../../../components/global/user-address/UserAddressFormModal';
import UserAddressDisplayEditBox from '../../../../components/global/user-address/UserAddressDisplayEditBox';
import PhoneInput from '../../../../components/global/PhoneInput';
import { isValidPhone } from '../../../../util/phone';
import { fileInputAccept } from '../../../../constants/photo-upload';
import { getFormikTouchedErrors } from '../../../../util/formik';
import Label from '../../../../components/global/Label';
import CustomControl from '../../../../components/global/CustomControl';
import {
  getIdentityFullName,
  getRosterIdentityClassName,
  getRosterIdentityGrade,
  getRosterIdentityMainLinkedIdentity,
  getRosterIdentityOrganizationRefId,
  getRosterIdentityTeacher,
} from '../../../../util/identity';
import FeatureBlock from '../../../../components/contextual/FeatureBlock';
import useBasename from '../../../../components/routing/useBasename';
import {
  canAccessNoPostToSocialOnBasename,
  canAccessSchoolFields,
  isYearbookEntity,
} from '../../../../util/product-permissions';
import { photoUploadFilter } from '../../../../util/photo-upload';
import BoxedCustomControl from '../../../../components/global/BoxedCustomControl';
import AlbumSearchSelect from './components/AlbumSearchSelect';
import DownloadPhotoButton from '../../../../containers/global/DownloadPhotoButtonContainer';
import uuidv4 from '../../../../util/uuid';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import RosterIdentityTagsDisplayBox from './components/RosterIdentityTagAssignment/RosterIdentityTagsDisplayBox';
import Select from '../../../../components/global/Select';
import { IDENTITY_GENDER } from '../../../../constants/identity';
import RosterIdentityTagAssignmentModal from './components/RosterIdentityTagAssignment/modal';
import { ACCOUNT_TYPE } from '../../views/AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';
import {
  HIERARCHY_GRADES_ENUM,
  HIERARCHY_GRADES_ENUM_LABELS,
} from '../../../../constants/hierarchy-grades';
import { isAlbum } from '../../../../util/album';
import { HierarchyItemForOwnerPurchaseJoins } from '../../../../graphql/queries/hierarchy-item';
import { formatPriceForDisplay } from '../../../../util/format-money';

const JOIN_TYPE = {
  ALBUM_FOLDER: 'ALBUM_FOLDER',
  ALBUMS: 'ALBUMS',
};

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  rosterIdentity: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    selfie_photo: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  selectedIdentity: PropTypes.shape({
    id: PropTypes.string,
    claimed: PropTypes.bool,
  }),
  identityId: PropTypes.string,
  loading: PropTypes.bool,
  photoUploadSuccess: PropTypes.func.isRequired,
  onRosterIdentityCreated: PropTypes.func,
  onRosterIdentityUpdated: PropTypes.func,
  defaultEditAddress: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string,
  albumFolderSelectionEnabled: PropTypes.bool,
  albumSelectionEnabled: PropTypes.bool,
  isSubscription: PropTypes.bool,
  organizationRefIdEnabled: PropTypes.bool,
  autoFocus: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      gender: PropTypes.string,
      uniform_enabled: PropTypes.bool,
      uniform_numbered: PropTypes.bool,
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teams: PropTypes.shape({
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                  coach_name: PropTypes.string,
                }),
              ),
            }),
          }),
        ),
      }),
    }),
  ),
  identityAlbumFolderTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      album_folder_tag: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        uniform_numbered: PropTypes.bool,
      }),
      jersey_number: PropTypes.string,
      uniform_album_folder_tag_level: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  pictureDayEnabled: PropTypes.bool,
};

const RosterIdentityUpdate = ({
  account,
  rosterIdentity,
  identityId,
  loading,
  photoUploadSuccess,
  onRosterIdentityCreated,
  onRosterIdentityUpdated,
  defaultEditAddress = false,
  onRequestClose,
  albumId,
  albumFolderId,
  albumFolderSelectionEnabled,
  albumSelectionEnabled,
  isSubscription,
  organizationRefIdEnabled = false,
  autoFocus = 'first_name',
  tags = [],
  identityAlbumFolderTags = [],
  selectedIdentity,
  pictureDayEnabled,
}) => {
  const basename = useBasename();

  const { data: { hierarchyItem } = {} } = useQuery(
    HierarchyItemForOwnerPurchaseJoins,
    {
      variables: { id: albumId ?? albumFolderId },
      skip: !albumId && !albumFolderId,
    },
  );

  const entity = hierarchyItem?.album ?? hierarchyItem?.album_folder;

  const isAlbumEntity = isAlbum(entity);
  const isYearbook = isYearbookEntity(entity);
  const ownerPurchasedJoinsEnabled = entity?.owner_purchased_joins_enabled;
  const isClaimed = !!selectedIdentity?.claimed || !!ownerPurchasedJoinsEnabled;

  // Price used for consent requirement: album price_whole_album or albumFolder price
  const entityPrice =
    entity?.price_whole_album?.value ?? entity?.price?.value ?? 0;

  // Used to get base phone number & address:
  const mainLinkedIdentity =
    getRosterIdentityMainLinkedIdentity(rosterIdentity);
  const fileInput = useRef(null);
  const [fileUrl, setFileUrl] = useState('');
  const [editAddress, setEditAddress] = useState(defaultEditAddress);
  const [joinType, setJoinType] = useState(
    albumFolderSelectionEnabled ? JOIN_TYPE.ALBUM_FOLDER : JOIN_TYPE.ALBUMS,
  );
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [preCreateTags, setPreCreateTags] = useState([]);
  const [isTagSelectionModalOpen, setIsTagSelectionModalOpen] = useState(false);
  const [selectedTagForEdit, setSelectedTagForEdit] = useState(null);

  const isCreate = !rosterIdentity?.id;
  const isMemberCreate = isCreate && (albumFolderId || albumId);

  const isRosterIdentityTagsEnabled = tags.length > 0;
  const {
    updating,
    updateError,
    updateSuccess,
    onRosterIdentityUpdate,
    onTagsCreate,
    onTagsDelete,
  } = useRosterIdentityUpdate({
    onPhotoUploadSuccess: ({ photoId, file: uploadedFile }) =>
      photoUploadSuccess(
        {
          id: photoId,
          file: uploadedFile,
          identityId: rosterIdentity?.id,
        },
        true,
      ),
    currentClaimedIdentity: selectedIdentity?.claimed,
  });

  const gradeOptions = useMemo(() => Object.values(HIERARCHY_GRADES_ENUM));

  const onFileChoose = event => {
    event.preventDefault();
    fileInput.current.click();
  };

  const isFormDisabled = values => {
    const hasContactInfo =
      values.phone_number?.trim() || values.email_address?.trim();

    const purchaseConstraintsViolated =
      ownerPurchasedJoinsEnabled &&
      values.grant_purchase &&
      ((entityPrice > 0 && !values.collection_consent) ||
        (!isAlbumEntity &&
          !!albumFolderId &&
          (selectedAlbums?.length ?? 0) === 0));

    return (
      !values.first_name ||
      !values.last_name ||
      !hasContactInfo ||
      updating ||
      purchaseConstraintsViolated
    );
  };

  const openTagModal = (tag = null) => {
    setSelectedTagForEdit(tag);
    setIsTagSelectionModalOpen(true);
  };

  const closeTagModal = () => {
    setIsTagSelectionModalOpen(false);
    setSelectedTagForEdit(null);
  };

  const handleTagsCreate = async (input, context) => {
    if (isCreate) {
      const updatedTag = selectedTagForEdit
        ? input?.album_folder_tags?.[0]
        : null;

      const updatedTags = [...preCreateTags].map(existingTag => {
        if (updatedTag && selectedTagForEdit.id === existingTag.id) {
          return {
            ...existingTag,
            album_folder_tag: {
              id: updatedTag.album_folder_tag_id,
              type: updatedTag.type,
              uniform_numbered: updatedTag.uniform_numbered,
            },
            jersey_number: updatedTag.uniform?.jersey_number,
            uniform_album_folder_tag_level: updatedTag.uniform
              ?.uniform_album_folder_tag_level_id
              ? {
                  id: updatedTag.uniform.uniform_album_folder_tag_level_id,
                  ...(updatedTag?.level ?? {}),
                }
              : null,
            uniform_album_folder_tag_level_team: updatedTag.uniform
              ?.uniform_album_folder_tag_level_team_id
              ? {
                  id: updatedTag.uniform.uniform_album_folder_tag_level_team_id,
                  ...(updatedTag?.team ?? {}),
                }
              : null,
          };
        }

        return existingTag;
      });

      const newTags = updatedTag
        ? []
        : input.album_folder_tags
            .map(tag => {
              if (updatedTags.find(({ id }) => id === tag.id)) {
                return null;
              }

              return {
                id: uuidv4(),
                album_folder_tag: {
                  id: tag.album_folder_tag_id,
                  type: tag.type,
                  uniform_numbered: tag.uniform_numbered,
                },
                jersey_number: tag.uniform?.jersey_number,
                uniform_album_folder_tag_level: tag.uniform
                  ?.uniform_album_folder_tag_level_id
                  ? {
                      id: tag.uniform.uniform_album_folder_tag_level_id,
                      ...(tag?.level ?? {}),
                    }
                  : null,
                uniform_album_folder_tag_level_team: tag.uniform
                  ?.uniform_album_folder_tag_level_team_id
                  ? {
                      id: tag.uniform.uniform_album_folder_tag_level_team_id,
                      ...(tag?.team ?? {}),
                    }
                  : null,
              };
            })
            .filter(Boolean);

      setPreCreateTags([...updatedTags, ...newTags]);
    } else {
      // In update mode, call the API directly
      await onTagsCreate(input, context);
    }
  };

  const handleTagsDelete = async (tag, context) => {
    if (isCreate) {
      setPreCreateTags(
        preCreateTags.filter(existingTag => existingTag.id !== tag.id),
      );
    } else {
      await onTagsDelete(tag.id, context);
    }
  };

  const onFormSubmit = async values => {
    if (isFormDisabled(values)) {
      return;
    }

    const {
      address,
      phone_number: phoneNumber,
      gender,
      selfie,
      grant_purchase: grantPurchase,
      collection_consent: collectionConsent,
      ...restValues
    } = values;

    const sanitizedPhoneNumber =
      phoneNumber?.startsWith('+') &&
      /^\+\d+$/.test(phoneNumber) &&
      phoneNumber.length < 7 // Check if phone number is just country code and min length
        ? null
        : phoneNumber;

    const purchasedAlbumIds = (() => {
      if (!ownerPurchasedJoinsEnabled || !grantPurchase) {
        return [];
      }

      return isAlbumEntity ? [albumId] : selectedAlbums.map(({ id }) => id);
    })();

    const isMarketingAccount =
      mainLinkedIdentity?.account?.type === ACCOUNT_TYPE.MARKETING;

    const rosterIdentityId = rosterIdentity?.id;
    const selectedIdentityId = selectedIdentity?.id;

    const payload = {
      ...restValues,
      date_of_birth: restValues.date_of_birth
        ? format(restValues.date_of_birth, 'yyyy-MM-dd')
        : null,
      identity_id: identityId,
      account_id: account.id,
      selected_identity_id: selectedIdentityId,
      email_address: values.email_address,
      roster_identity_id: rosterIdentityId,
      linked_identities: [
        {
          id:
            !isMarketingAccount || !!address?.address_line_1
              ? mainLinkedIdentity?.id
              : undefined,
          phone_number: sanitizedPhoneNumber,
          addresses: address?.address_line_1 ? [address] : [],
        },
      ],
      linked_album_ids: (() => {
        if (!(albumId || joinType === JOIN_TYPE.ALBUMS)) {
          return [];
        }

        return albumId ? [albumId] : selectedAlbums.map(({ id }) => id);
      })(),
      linked_album_folder_ids:
        albumFolderId && joinType === JOIN_TYPE.ALBUM_FOLDER
          ? [albumFolderId]
          : [],
      owner_purchased_album_ids: purchasedAlbumIds,
      selfie,
      create_album_folder_tags: isCreate ? preCreateTags : undefined,
    };

    if (gender) {
      payload.gender = gender;
    }

    const latestRosterIdentity = await onRosterIdentityUpdate(payload);

    if (!latestRosterIdentity) {
      // Possible error
      return;
    }

    if (isCreate || isMarketingAccount) {
      onRosterIdentityCreated?.(latestRosterIdentity);
      onRequestClose();
    } else {
      onRosterIdentityUpdated?.(latestRosterIdentity);

      // If editing only the address, close the modal immediately after save
      if (defaultEditAddress) {
        onRequestClose();
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const photo = fileUrl
    ? {
        thumbnail_urls: [{ width: 0, height: 0, url: fileUrl }],
        id: 'local-upload',
      }
    : (rosterIdentity?.selfie_photo ??
      rosterIdentity?.current_matching_photo ??
      rosterIdentity?.selfie_photo_urls?.records?.[0]?.selfie_photo ??
      rosterIdentity?.selfie_photo_filenames?.records?.[0]?.selfie_photo ??
      mainLinkedIdentity?.identity?.current_photo);

  const photoThumbnail = getThumbBySize(
    photo?.thumbnail_urls ?? [],
    IMAGE_SIZE_CODE.MEDIUM,
  );

  const mainLinkedIdentityPhotoOutdated =
    mainLinkedIdentity?.identity?.has_outdated_selfie;

  const validate = values => {
    const errors = {};

    if (
      values.email_address &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email_address)
    ) {
      errors.email_address = 'Please provide a valid email address.';
    }

    if (
      !mainLinkedIdentity?.id &&
      !!values?.address?.address_line_1 &&
      !isValidPhone(values.phone_number)
    ) {
      errors.phone_number =
        'You must provide a valid phone number when creating an address for an identity.';
    }

    if (
      values.phone_number &&
      !/^\+\d+$/.test(values.phone_number) && // If not just country code
      !isValidPhone(values.phone_number)
    ) {
      errors.phone_number =
        'Provided phone number is not valid. Please provide a valid one.';
    }

    const requiresRefId =
      isYearbook && isCreate && values.grade !== HIERARCHY_GRADES_ENUM.STAFF;

    if (requiresRefId && !(values.organization_ref_id ?? '').trim()) {
      errors.organization_ref_id =
        'Except for STAFF members, Ref ID is required for all yearbook event members.';
    }

    return errors;
  };

  const photoContainerClasses = classNames({
    'text-center': true,
    'mb-3': !photo?.id,
  });

  // Hide join toggle for now since is confusing for PROs
  const shouldHideJoinToggle = classNames({
    'd-none': true,
  });

  return (
    <Formik
      validate={validate}
      initialValues={{
        selfie: null,
        first_name: rosterIdentity?.first_name ?? '',
        last_name: rosterIdentity?.last_name ?? '',
        gender: rosterIdentity?.gender ?? '',
        phone_number: mainLinkedIdentity?.phone_number ?? '',
        address: mainLinkedIdentity?.addresses?.records[0] ?? {
          // Pass the name of the identity
          // as the default on an empty address
          // so that it's prepopulated.
          name: getIdentityFullName(rosterIdentity),
        },
        date_of_birth: rosterIdentity?.date_of_birth
          ? parse(rosterIdentity.date_of_birth, 'yyyy-MM-dd', new Date())
          : null,
        photo_share_blocked: rosterIdentity?.photo_share_blocked,
        organization_ref_id: getRosterIdentityOrganizationRefId({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        email_address:
          rosterIdentity?.email_addresses?.records[0]?.email_address,
        grade: getRosterIdentityGrade({
          rosterIdentity,
          albumId,
          albumFolderId,
          rawValue: true,
        }),
        teacher_name: getRosterIdentityTeacher({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        class_name: getRosterIdentityClassName({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        claimed_identity: isClaimed,
        grant_purchase: false,
        collection_consent: false,
      }}
      onSubmit={onFormSubmit}
      render={({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        touched,
        dirty,
      }) => {
        const formikErrors = Object.values(
          getFormikTouchedErrors(errors, touched),
        );
        const usedTagIds =
          identityAlbumFolderTags &&
          identityAlbumFolderTags.map(tag => tag.album_folder_tag.id);

        const availableTags =
          tags &&
          tags.filter(
            tag => tag.gender === values.gender && !usedTagIds.includes(tag.id),
          );

        return (
          <Fragment>
            <form method="POST" onSubmit={handleSubmit}>
              <div className={photoContainerClasses}>
                {photoThumbnail && (
                  <p className="my-1 text-xs text-normal text-light">
                    Hover to enlarge reference photo
                  </p>
                )}
                <a onClick={onFileChoose} href="#">
                  <IdentityAvatarContainer
                    size={AVATAR_SIZE_72}
                    identity={{
                      id:
                        fileUrl || !rosterIdentity?.id
                          ? uuidv4()
                          : rosterIdentity.id,
                      ...(rosterIdentity || {}),
                      name: rosterIdentity?.first_name,
                      current_matching_photo: photo,
                    }}
                    selfieType="matching"
                    data-tip=""
                    data-for={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                    data-place="top"
                  >
                    {photoThumbnail && (
                      <ReactTooltip
                        delayHide={250}
                        className="member-photos-tooltip"
                        id={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                        backgroundColor="transparent"
                        arrowColor="transparent"
                        effect="solid"
                      >
                        <img src={photoThumbnail.url} alt="Identity Selfie" />
                      </ReactTooltip>
                    )}
                  </IdentityAvatarContainer>
                  <span className="d-block mt-1 text-sm">
                    {photo ? 'Edit' : 'Add'} reference photo
                  </span>
                </a>
                {mainLinkedIdentityPhotoOutdated && photo && (
                  <p className="text-danger text-xs">
                    The selfie shown for this identity is outdated and <br />
                    won&rsquo;t be used for matching. Please update it to
                    restore matching.
                  </p>
                )}
              </div>
              {photo?.id && (
                <div className="text-center mb-3">
                  <DownloadPhotoButton
                    downloadOriginal={!!photo?.is_owner}
                    photoId={photo?.id}
                    photoSize={IMAGE_SIZE_CODE.LARGE2X}
                    btnStyle="link"
                    btnSize="sm"
                  >
                    Download current reference photo
                  </DownloadPhotoButton>
                </div>
              )}

              <input
                id="upload-photo"
                type="file"
                accept={fileInputAccept}
                onChange={event => {
                  const [eventFile] = event.target.files;

                  if (!photoUploadFilter(eventFile)) {
                    return;
                  }

                  setFileUrl(URL.createObjectURL(eventFile));

                  handleChange({
                    target: {
                      name: 'selfie',
                      value: eventFile,
                    },
                  });
                }}
                ref={fileInput}
                hidden
                data-testid="roster-identity-update-upload-photo"
              />

              <div className="row">
                <div className="col-6">
                  <FormGroup
                    id="first_name"
                    name="first_name"
                    label="First Name"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'first_name'}
                    autoComplete="off"
                  />
                </div>

                <div className="col-6">
                  <FormGroup
                    id="last_name"
                    name="last_name"
                    label="Last Name"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'last_name'}
                    autoComplete="off"
                  />
                </div>
                <div className="col-6">
                  <Select
                    id="gender"
                    label="Gender"
                    value={values.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'gender'}
                    disabled={
                      !!rosterIdentity?.gender ||
                      (updateSuccess && values.gender)
                    }
                    autoComplete="off"
                  >
                    <option value="">Select</option>
                    {Object.values(IDENTITY_GENDER).map(gender => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </Select>
                </div>
                {!isMemberCreate && (
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="birthDate" labelNote="optional">
                        Birthday
                      </Label>

                      <DatePicker
                        id="birthDate"
                        name="date_of_birth"
                        className="form-control"
                        isClearable
                        maxDate={new Date()}
                        selected={values.date_of_birth}
                        onChange={date => {
                          date?.setHours(0, 0, 0, 0);

                          handleChange({
                            target: {
                              name: 'date_of_birth',
                              value: date,
                            },
                          });

                          handleBlur?.({
                            target: {
                              name: 'date_of_birth',
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="col-6">
                  <FormGroup
                    id="email_address"
                    label="Email"
                    value={values.email_address}
                    onChange={handleChange}
                    disabled={
                      !!rosterIdentity?.email_addresses?.records[0]
                        ?.email_address ||
                      (updateSuccess && values.email_address)
                    }
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'email_address'}
                    autoComplete="off"
                  />
                </div>

                <div className="col-6">
                  <PhoneInput
                    id="phone_number"
                    label="Mobile Phone Number"
                    value={values.phone_number || '+1'}
                    onChange={phone => {
                      handleChange({
                        target: {
                          name: 'phone_number',
                          value: phone,
                        },
                      });
                    }}
                    required={isCreate || !!values.address}
                    disabled={
                      !!mainLinkedIdentity?.phone_number ||
                      (updateSuccess && values.phone_number)
                    }
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'phone_number'}
                    autoComplete="off"
                  />
                </div>
                {(organizationRefIdEnabled ||
                  (isYearbook &&
                    isCreate &&
                    values.grade !== HIERARCHY_GRADES_ENUM.STAFF)) && (
                  <div className="col-6">
                    <FormGroup
                      id="organization_ref_id"
                      name="organization_ref_id"
                      label="Ref ID"
                      value={values.organization_ref_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus={autoFocus === 'organization_ref_id'}
                    />
                  </div>
                )}
                {gradeOptions.length > 0 && canAccessSchoolFields(basename) && (
                  <Fragment>
                    <div className="col-6">
                      <Label id="grade">Grade</Label>
                      <select
                        id="grade"
                        name="grade"
                        value={values.grade}
                        className="form-control"
                        onChange={handleChange}
                        placeholder="Select a grade..."
                      >
                        <option value="">Select a grade...</option>
                        {gradeOptions.map(gradeEnum => (
                          <option key={gradeEnum} value={gradeEnum}>
                            {HIERARCHY_GRADES_ENUM_LABELS[gradeEnum]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <FormGroup
                        id="teacher_name"
                        name="teacher_name"
                        label="Teacher"
                        value={values.teacher_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'teacher_name'}
                      />
                    </div>
                    <div className="col-12">
                      <FormGroup
                        id="class_name"
                        name="class_name"
                        label="Home room #"
                        value={values.class_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'class_name'}
                      />
                    </div>
                  </Fragment>
                )}
                <FeatureBlock
                  condition={canAccessNoPostToSocialOnBasename(basename)}
                >
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="noPostToSocial">No post to social</Label>
                      <CustomControl
                        type="checkbox"
                        id="npts"
                        size="sm"
                        checked={values.photo_share_blocked}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: 'photo_share_blocked',
                              value: !values.photo_share_blocked,
                            },
                          })
                        }
                        label="Do not allow photos featuring this member to be posted to social via apps."
                      />
                    </div>
                  </div>
                </FeatureBlock>
                <FeatureBlock
                  condition={
                    !ownerPurchasedJoinsEnabled &&
                    (selectedIdentity || isMemberCreate)
                  }
                >
                  <div className="col-12">
                    <div className="form-group">
                      <Label id="claimIdentity">Claimed Identity</Label>
                      <CustomControl
                        type="checkbox"
                        id="claimIdentity"
                        size="sm"
                        checked={values.claimed_identity}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: 'claimed_identity',
                              value: !values.claimed_identity,
                            },
                          })
                        }
                        label="Member will receive notifications for matches. Email-only members will always be claimed in order to receive email notifications."
                      />
                    </div>
                  </div>
                </FeatureBlock>
              </div>
              {albumFolderId && pictureDayEnabled && (
                <div className={shouldHideJoinToggle}>
                  <div className="row mb-2">
                    {albumFolderSelectionEnabled && (
                      <div className="col-12">
                        <BoxedCustomControl
                          id="album-folder-join"
                          type="checkbox"
                          label="Join the gallery"
                          description="Join all albums within this gallery."
                          onChange={() => {
                            setJoinType(JOIN_TYPE.ALBUM_FOLDER);
                          }}
                          checked={joinType === JOIN_TYPE.ALBUM_FOLDER}
                        />
                      </div>
                    )}
                  </div>
                  {albumFolderSelectionEnabled && albumSelectionEnabled && (
                    <div className="col-12">
                      <Label
                        id="label-or"
                        labelType="expanded"
                        className="w-100 text-center my-1"
                      >
                        OR
                      </Label>
                    </div>
                  )}
                  {albumSelectionEnabled && (
                    <div className="col-12">
                      <BoxedCustomControl
                        id="album-join"
                        type="checkbox"
                        label="Join albums"
                        description={`Specify the albums you want this person to be ${
                          isSubscription
                            ? 'subscribed to'
                            : 'able to preview their photos on'
                        }.`}
                        onChange={() => {
                          setJoinType(JOIN_TYPE.ALBUMS);
                        }}
                        checked={joinType === JOIN_TYPE.ALBUMS}
                      >
                        {joinType === JOIN_TYPE.ALBUMS && (
                          <AlbumSearchSelect
                            selectedAlbums={selectedAlbums}
                            albumFolderId={albumFolderId}
                            onSelectedAlbumsChange={newSelectedAlbums =>
                              setSelectedAlbums(newSelectedAlbums)
                            }
                          />
                        )}
                      </BoxedCustomControl>
                    </div>
                  )}
                </div>
              )}

              {ownerPurchasedJoinsEnabled && (
                <div className="row mb-1">
                  <div className="col-12">
                    <Label id="grant-purchase">Grant purchase</Label>
                    <CustomControl
                      type="checkbox"
                      id="grant_purchase"
                      size="sm"
                      checked={values.grant_purchase}
                      onChange={() => {
                        const next = !values.grant_purchase;
                        handleChange({
                          target: {
                            name: 'grant_purchase',
                            value: !values.grant_purchase,
                          },
                        });
                        if (next && entityPrice === 0) {
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: true,
                            },
                          });
                        }
                        if (!next) {
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: false,
                            },
                          });
                        }
                      }}
                      label={
                        isAlbumEntity
                          ? 'Grant purchase to consumer for this album'
                          : 'Grant purchase to consumer for selected albums'
                      }
                    />
                  </div>

                  {values.grant_purchase && !isAlbumEntity && albumFolderId && (
                    <div className="col-12 mb-1">
                      <AlbumSearchSelect
                        selectedAlbums={selectedAlbums}
                        albumFolderId={albumFolderId}
                        onSelectedAlbumsChange={newSelectedAlbums =>
                          setSelectedAlbums(newSelectedAlbums)
                        }
                      />
                    </div>
                  )}

                  {values.grant_purchase && entityPrice > 0 && (
                    <div className="col-12 mb-1">
                      <CustomControl
                        id="collection-consent"
                        type="checkbox"
                        name="collection_consent"
                        checked={values.collection_consent}
                        onClick={() =>
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: !values.collection_consent,
                            },
                          })
                        }
                        label={
                          <span>
                            I have collected{' '}
                            <span className="text-extra-bold">
                              ${formatPriceForDisplay(entityPrice)}
                            </span>{' '}
                            directly from{' '}
                            <span className="text-primary">
                              {values.first_name ?? 'this person'}
                            </span>{' '}
                            and am responsible for Waldo&apos;s fee for this
                            subscription
                          </span>
                        }
                        size="sm"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              )}

              {isRosterIdentityTagsEnabled && (
                <RosterIdentityTagsDisplayBox
                  tags={tags}
                  className="mb-2"
                  identityAlbumFolderTags={identityAlbumFolderTags}
                  preCreateAlbumFolderTags={preCreateTags}
                  sportsTagsDelete={handleTagsDelete}
                  albumFolderId={albumFolderId}
                  identityId={identityId}
                  mainLinkedIdentity={mainLinkedIdentity}
                  gender={values.gender}
                  onEditSportsTag={openTagModal}
                />
              )}

              <UserAddressDisplayEditBox
                address={values.address}
                onClick={() => {
                  setEditAddress(true);
                }}
                className="form-group"
              />

              {(updateError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {updateError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              {updateSuccess && (
                <Alert type="success" dismissible>
                  {isCreate ? 'Created' : 'Updated'} successfully!
                </Alert>
              )}

              <div className="text-right">
                <Button
                  type="submit"
                  disabled={!dirty || isFormDisabled(values)}
                  btnSize="sm"
                  className="m-0"
                >
                  {(() => {
                    if (updating) {
                      return 'Loading...';
                    }

                    return rosterIdentity ? 'Update' : 'Create';
                  })()}
                </Button>
              </div>
            </form>

            <RosterIdentityTagAssignmentModal
              forceOpen={isTagSelectionModalOpen}
              albumFolderId={albumFolderId}
              identityId={identityId}
              tags={selectedTagForEdit ? tags : availableTags}
              selectedTag={selectedTagForEdit}
              identityAlbumFolderTags={
                selectedTagForEdit ? [selectedTagForEdit] : []
              }
              onComplete={() => {
                // In update mode, save roster identity right away if gender is selected
                // In create mode, don't trigger handleSubmit - tags are saved locally
                if (!isCreate && values.gender !== rosterIdentity?.gender) {
                  handleSubmit();
                }
                closeTagModal();
              }}
              sportsTagsCreate={handleTagsCreate}
              onCancel={closeTagModal}
              includeTeamLevelData={isCreate}
            />

            <UserAddressFormModal
              forceOpen={editAddress}
              address={values.address}
              onCancel={() => {
                setEditAddress(false);

                if (defaultEditAddress) {
                  onRequestClose();
                }
              }}
              onSuccess={address => {
                setEditAddress(false);
                handleChange({
                  target: {
                    name: 'address',
                    value: address,
                  },
                });

                if (!isCreate) {
                  // When updating, save right away:
                  handleSubmit();
                }
              }}
            />
          </Fragment>
        );
      }}
    />
  );
};

RosterIdentityUpdate.propTypes = propTypes;

export default RosterIdentityUpdate;
